import { getContentSchemaFromContentFul } from '@Redux/actions/common';
import { APPLY_COUPON_CMS_DATA, NEW_COUPON_FLOW_FLAG } from '@Utils/constants';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../store.type';
import { saveCMSCouponData, saveCouponData } from '@Redux/actions/transaction';
import { getRewardCouponsList } from '@Redux/actions/dashboard';
import { isEmpty } from 'lodash';
import { Coupon, couponCMSDataType, formatCouponData } from '../components/referral-coupon/utils';
import { saveNonFormattedCouponData } from '@Redux/actions/transaction/transaction-action';
import { getFlagValue, isFlagEnabled } from '@Redux/actions/flagsmith';
import { getCountryCode } from '@Redux/selectors';

export const useCouponData = (props: {
  couponCMSData: couponCMSDataType;
  source_currency: string;
  source_amount: number;
  destination_currency: string;
  storedNonFormattedCouponData: Coupon[];
}) => {
  const {
    couponCMSData: propCouponCMSData,
    source_currency,
    source_amount,
    destination_currency,
    storedNonFormattedCouponData
  } = props;

  const [couponList, setCouponList] = useState<Coupon[]>([]);
  const [couponCMSData, setCouponCMSData] = useState(APPLY_COUPON_CMS_DATA);
  const dispatch = useDispatch<AppDispatch>();
  const countryCode = useSelector(getCountryCode);

  const fetchSendMoneyCMSData = async () => {
    // Fetch CMS data only if its not available in Redux store
    if (isEmpty(propCouponCMSData)) {
      const response = await getContentSchemaFromContentFul(
        'pageSendMoney',
        'MOBILE_SEND_MONEY_TRANSFER'
      );
      if (response?.labels?.value?.applyCouponPage) {
        const applyCouponPageData = response.labels.value.applyCouponPage;
        setCouponCMSData(applyCouponPageData);
        dispatch(saveCMSCouponData(applyCouponPageData));
      }
    }
  };

  const fetchRewardCouponList = async () => {
    try {
      const couponListResponse = await dispatch(getRewardCouponsList({ source_currency }));

      if (couponListResponse?.data?.success) {
        dispatch(saveNonFormattedCouponData(couponListResponse.data.data || []));
        const couponData =
          formatCouponData(couponListResponse.data.data, source_amount, destination_currency) || [];
        setCouponList(couponData);
        dispatch(saveCouponData(couponData));
      }
    } catch (error) {
      setCouponList([]);
    }
  };

  const saveFormattedCouponData = (couponList: Coupon[]) => {
    if (couponList) {
      const formattedData = formatCouponData(couponList, source_amount, destination_currency) || [];
      setCouponList(formattedData);
      dispatch(saveCouponData(formattedData));
    }
  };

  const isCouponEnabled = () => {
    const couponEnabledCountries = isFlagEnabled(NEW_COUPON_FLOW_FLAG)
      ? getFlagValue(NEW_COUPON_FLOW_FLAG) ?? []
      : [];
    return couponEnabledCountries.includes(countryCode);
  };

  useEffect(() => {
    source_currency && isCouponEnabled() && fetchRewardCouponList();
    source_currency && fetchSendMoneyCMSData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source_currency]);

  // Reformat coupons when source amount changes
  useEffect(() => {
    source_amount && couponList?.length > 0 && saveFormattedCouponData(couponList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source_amount]);

  useEffect(() => {
    if (destination_currency && !isEmpty(storedNonFormattedCouponData)) {
      saveFormattedCouponData(storedNonFormattedCouponData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination_currency]);

  return {
    couponList,
    couponCMSData
  };
};
